import { z } from 'zod'
import { listingPriceLevelSchema, timeRangeSchema } from './listing'

/** Website reads one business may start per day; the read itself costs no credits. */
export const PLACE_FROM_WEBSITE_DAILY_LIMIT = 10
export const PLACE_FROM_WEBSITE_MAX_PHOTOS = 8

export const placeFromWebsiteRequestSchema = z.object({
  url: z.string().trim().min(1).max(2048),
  locale: z.enum(['de', 'en']).default('de'),
})

export type PlaceFromWebsiteRequest = z.infer<typeof placeFromWebsiteRequestSchema>

const weekHoursSchema = z.object({
  '0': z.array(timeRangeSchema),
  '1': z.array(timeRangeSchema),
  '2': z.array(timeRangeSchema),
  '3': z.array(timeRangeSchema),
  '4': z.array(timeRangeSchema),
  '5': z.array(timeRangeSchema),
  '6': z.array(timeRangeSchema),
})

/** Opening hours per weekday, "0" being Sunday; a closed day is an empty list. */
export type WeekHours = z.infer<typeof weekHoursSchema>

/** A photo taken from the website, already checked and re-encoded, not stored anywhere yet. */
export const websitePhotoSchema = z.object({
  sourceUrl: z.string(),
  dataBase64: z.string(),
  mimeType: z.literal('image/webp'),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
})

export type WebsitePhoto = z.infer<typeof websitePhotoSchema>

/**
 * A place profile read from its website, for the business to review before anything is saved.
 * `source` says whether the page described itself for search engines (`structured`), the
 * model read it (`ai`), or both.
 */
export const placeFromWebsiteResponseSchema = z.object({
  source: z.enum(['structured', 'ai', 'structured_and_ai']),
  name: z.string(),
  description: z.string(),
  location: z.object({
    street: z.string(),
    postalCode: z.string(),
    city: z.string(),
    country: z.string(),
    coordinates: z.object({ lat: z.number(), lng: z.number() }).nullable(),
  }),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string(),
  instagram: z.string().nullable(),
  facebook: z.string().nullable(),
  twitter: z.string().nullable(),
  hours: weekHoursSchema,
  tags: z.object({
    mainCategoryId: z.string(),
    subCategoryTags: z.array(z.string()),
  }),
  priceRange: z.string().nullable(),
  /** `priceRange` as 1 to 4 currency signs, for the listing's price level. */
  priceLevel: listingPriceLevelSchema.nullable(),
  photos: z.array(websitePhotoSchema),
})

export type PlaceFromWebsiteResponse = z.infer<typeof placeFromWebsiteResponseSchema>
